import {
  APIError,
  PagedResult,
  Role,
  User,
  UserQuery,
  users,
  roles as rolesAPI,
  password as passwordAPI,
} from "marvin-auth-kit";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { v4 as uuidv4 } from "uuid";
import axios, { AxiosResponse } from "axios";
import { NewUser } from "../types";

export const usePagedUsers = (config: string, query: UserQuery) => {
  function dedupeUsers(result: PagedResult<User>) {
    const dedupedData = result.data.reduce(
      (stack: User[], currentUser: User) => {
        if (
          stack.find((user: User) => user.id === currentUser.id) === undefined
        ) {
          return [...stack, currentUser];
        }
        return [...stack];
      },
      []
    );

    return { ...result, data: dedupedData, totalCount: dedupedData.length };
  }
  /**
   * Fetches all users. Because the `role` filter is a AND filter we need multiple calls and dedupe the results!
   * @returns PagedResult<User>>
   */
  async function fetch() {
    const { role, ...restQuery } = query;
    const response = await (role || [config]).reduce(
      (chain: Promise<PagedResult<User>>, role: string | Role) => {
        return chain.then(async (result: PagedResult<User>) => {
          const response = await users.query({ ...restQuery, role: [role] });
          return {
            ...response,
            data: result.data.concat(response.data),
            totalCount: result.totalCount + response.totalCount,
          };
        });
      },
      Promise.resolve({
        totalCount: 0,
        data: [],
        offset: query.offset || 0,
        limit: query.limit || 10,
      })
    );

    return dedupeUsers(response);
  }
  return useQuery(["users", query], fetch);
};

export const useConfigRestrictedRole = (config: string) => {
  async function fetch() {
    const response = await rolesAPI.query({ role: `${config}-restricted` });

    return response.data.shift();
  }

  return useQuery(["role", "restricted", config], fetch);
};

const useConfigRole = (config: string) => {
  async function fetch() {
    const response = await rolesAPI.query({ role: config });

    return response.data.shift();
  }

  return useQuery(["role", "config", config], fetch);
};

export const useUpdateUser = (config: string) => {
  const queryClient = useQueryClient();
  async function update(user: User) {
    const response = await users.update(user);
    return response;
  }

  return useMutation(["user"], update, {
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
    },
  });
};

export const useCreateUser = (config: string) => {
  const queryClient = useQueryClient();
  async function create(user: Omit<User, "id">) {
    const response = await users.create({ ...user });
    await passwordAPI.request(user.email);
    return response;
  }

  return useMutation(["user"], create, {
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
    },
  });
};

export const useEmptyUser = (config: string) => {
  const { data: configRole } = useConfigRole(config);

  const NEW_USER = {
    email: "",
    password: uuidv4(),
    enabled: true,
    roles: [],
  };

  async function fetch() {
    if (configRole)
      return Promise.resolve<NewUser>({
        ...NEW_USER,

        roles: [configRole],
      });
    //backup.. will never happen als the query is disabled until roles are loaded.
    return Promise.resolve<NewUser>(NEW_USER);
  }

  return useQuery(["user", "new"], fetch, {
    enabled: configRole !== undefined,
  });
};

export const useDeleteUser = (config: string) => {
  const queryClient = useQueryClient();
  async function remove(user: User) {
    const response = await users.delete(user.id);
    return response;
  }

  return useMutation(["user"], remove, {
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
    },
  });
};

export interface RegistrationArguments {
  email: string;
  password: string;
  metadata: { gdprAccepted: boolean; lastRegistrationConfig: string };
}

export const useRegistration = (config: string) => {
  async function create(variables: RegistrationArguments) {
    const response = await axios.post<
      boolean,
      AxiosResponse<boolean, APIError[]>,
      RegistrationArguments
    >(`/api/${config}/register`, variables);
    return response.data;
  }

  return useMutation(["register"], create);
};

export function useUserAccessGrantedEMail(config: string) {
  async function mutateFn(user: User) {
    const response = await axios.get<boolean>(
      `/api/${config}/users/${user.id}/access_granted`
    );
    return response.data;
  }

  return useMutation(["access-granted", config], mutateFn, {
    retry: false,
  });
}
